import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { FilterPresets } from '../ui/filter-presets';
import { Undo2, Search, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../services/api';
import { useProfile } from '../../contexts/ProfileContext';
import { useToast } from '../../contexts/ToastContext';
import { hasAdminPrivileges, NEW_BROKER_PRIVILEGED_EMAILS } from '../../utils/role';
import { hasBootcampAccess } from '../../utils/subscriptionAccess';
import { AI_REFRESH_MS, aiKpColor, aiLevel, aiLevelName, aiProbColor, aiShapeColor, aiShapeFilterColor, aiShapeName, aiShapeRank, aiShapeShort, fmtYmd, hasStrongWarning, sameAccDistDate, type AccDistAiResults, type AiStock } from '../../utils/accdistAi';
import { fetchAccumulationDistributionData, fetchAccumulationDistributionDates, fetchBrokerAccumulationDistributionData } from '../../utils/accumulationDistributionBlob';
import { fetchVolumeData, fetchBrokerVolumeData, type VolumeType } from '../../utils/volumeBlob';
import { fetchBrokerList } from '../../utils/brokerMetadataBlob';
import { evaluateFilterExpression, parseCrossColumnReference, evaluateCrossColumn, type FilterCell, type AtomEvaluator, type CrossRefCondition } from '../../utils/columnFilter';
import { cleanFilters, type FilterSettings } from '../../utils/filterPresets';

// Broker colors same as BrokerSummaryPage (merah=foreign, ijo=government, putih=local)
const FOREIGN_BROKERS = ["AG","AH","AI","AK","BK","BQ","CG","CS","DP","DR","DU","FS","GW","HD","KK","KZ","LH","LG","LS","MS","RB","RX","TX","YP","YU","ZP"];
const GOVERNMENT_BROKERS = ['CC','NI','OD','DX'];
const getBrokerColorClass = (code: string): string => {
  if (GOVERNMENT_BROKERS.includes(code)) return 'text-green-500 font-semibold';
  if (FOREIGN_BROKERS.includes(code)) return 'text-red-400 font-semibold';
  return 'text-white font-semibold';
};

export interface AccumulationData {
  symbol: string;
  price: number;
  volume: number;
  value: number;
  percent1d: number;
  ma20: number;
  ma22: number;
  ma30: number;
  vol_ma22: number;
  vol_ma30: number;
  ma22_prev: number;
  ma30_prev: number;
  price_color: number;
  vol_color: number;
  vs_ratio?: number;
  vs_comp?: number;
  trend: string;
  momentum: number;
  insider?: number;
  periods: Record<string, {
    val: number;
    vol: number;
    val_pct: number;
    vol_pct: number;
    comp: number;
    xl: number;
    xc: number;
    nsb?: number | undefined; // order non split beli 3 net buyer teratas (undefined = data tidak ada)
    nss?: number | undefined; // order non split jual 3 net seller teratas
  }>;
  // Gerak harga per hari d0..d5 (hover D0–D4): c = %C (close vs close kemarin), h = %H (high vs close kemarin)
  moves?: Record<string, { c: number; h: number }>;
}

// Convert backend JSON from CSV to frontend format
const convertBackendToFrontend = (backendData: any[]): AccumulationData[] => {
  const uniqueItems = new Map<string, any>();
  for (const item of backendData) {
    const symbol = item.Stock || item.Ticker || '';
    if (symbol && !uniqueItems.has(symbol)) {
      uniqueItems.set(symbol, item);
    }
  }

  return Array.from(uniqueItems.values()).map(item => {
    const symbol = item.Stock || item.Ticker || '';
    const price = Number(item.price) || 0;
    const volume = Number(item.volume) || 0;
    const value = Number(item.value) || 0;
    const percent1d = Number(item.price_chg) || 0;
    const ma20 = Number(item.ma20_above) || 0;
    const ma22 = Number(item.ma22) || 0;
    const ma30 = Number(item.ma30) || 0;
    const vol_ma22 = Number(item.vol_ma22) || 0;
    const vol_ma30 = Number(item.vol_ma30) || 0;
    const ma22_prev = Number(item.ma22_prev) || 0;
    const ma30_prev = Number(item.ma30_prev) || 0;
    const price_color = Number(item.price_color) || 0;
    const vol_color = Number(item.vol_color) || 0;
    const vs_ratio = item.vs_ratio !== undefined ? Number(item.vs_ratio) : undefined;
    const vs_comp = item.vs_comp !== undefined ? Number(item.vs_comp) : undefined;
    const momentum = item.momentum !== undefined ? Number(item.momentum) : 1; // Default SW
    const insider = item.insider !== undefined ? Number(item.insider) : undefined;
    
    // Backend returns 0 (Down), 1 (Sideways), 2 (Up)
    let trend = 'SW';
    if (item.trend !== undefined) {
      const t = Number(item.trend);
      if (t === 2) trend = 'Up Trend';
      else if (t === 0) trend = 'Down Trend';
    }

    const periods: AccumulationData['periods'] = {};
    const optNum = (v: unknown): number | undefined => (v === undefined || v === null || v === '' ? undefined : Number(v));
    
    // Extract all period prefixes
    const periodPrefixes = [
      'd0','d1','d2','d3','d4','d5','w1','w2','w3','w4',
      'fd0','fd1','fd2','fd3','fd4','fd5','fw1','fw2','fw3','fw4',
      'cd0','cd1','cd2','cd3','cd4','cd5','cw1','cw2','cw3','cw4',
      'cfd0','cfd1','cfd2','cfd3','cfd4','cfd5','cfw1','cfw2','cfw3','cfw4'
    ];

    for (const p of periodPrefixes) {
      periods[p] = {
        val: Number(item[`${p}_val`]) || 0,
        vol: Number(item[`${p}_vol`]) || 0,
        val_pct: Number(item[`${p}_val_pct`]) || 0,
        vol_pct: Number(item[`${p}_vol_pct`]) || 0,
        comp: Number(item[`${p}_compat`]) || 0,
        xl: item[`${p}_xl`] !== undefined ? Number(item[`${p}_xl`]) : -1,
        xc: item[`${p}_xc`] !== undefined ? Number(item[`${p}_xc`]) : -1,
        nsb: optNum(item[`${p}_nsb`]),
        nss: optNum(item[`${p}_nss`]),
      };
    }
    const moves: Record<string, { c: number; h: number }> = {};
    for (let i = 0; i <= 5; i++) {
      const c = optNum(item[`d${i}_pc`]), h = optNum(item[`d${i}_ph`]);
      if (c !== undefined && h !== undefined && Number.isFinite(c) && Number.isFinite(h)) moves[`d${i}`] = { c, h };
    }

    return {
      symbol,
      price,
      volume,
      value,
      percent1d,
      ma20,
      ma22,
      ma30,
      vol_ma22,
      vol_ma30,
      ma22_prev,
      ma30_prev,
      price_color,
      vol_color,
      vs_ratio,
      vs_comp,
      trend,
      momentum,
      insider,
      periods,
      moves
    };
  });
};

const formatNumberWithAbbreviation = (num: number | null): string => {
  if (num === null || num === 0) return '-';
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e9) {
    return sign + (absNum / 1e9).toFixed(1) + 'B';
  } else if (absNum >= 1e6) {
    return sign + (absNum / 1e6).toFixed(1) + 'M';
  } else if (absNum >= 1e3) {
    return sign + (absNum / 1e3).toFixed(1) + 'K';
  } else {
    return sign + absNum.toFixed(1); 
  }
};

// pct = persen akumulasi (vol_pct; IHSG: val_pct) — gate tambahan utk ijo tua/merah tua:
// ijo tua hanya jika akumulasi > 4%, merah tua hanya jika minus > 4%.
// nsb/nss = order non split beli top 3 net buyer / jual top 3 net seller (kriteria ke-5): ijo tua butuh
// nsb < nss, merah tua butuh nsb > nss. Diabaikan bila datanya tidak ada (acc_dist lama).
const getTextColor = (val: number, comp: number, xl: number, xc: number, pct: number, nsb?: number, nss?: number): string => {
  const hasNs = nsb !== undefined && nss !== undefined;
  if (val < 0) {
    return (comp > 0 && xl === 0 && xc === 0 && pct < -4 && (!hasNs || nsb > nss)) ? 'text-[#ff0000]' : 'text-[#ffa500]';
  } else if (val > 0) {
    return (comp < 0 && xl === 1 && xc === 1 && pct > 4 && (!hasNs || nsb < nss)) ? 'text-[#00ff00]' : 'text-[#b2ff00]';
  }
  return 'text-muted-foreground';
};

const getPercent1dColor = (value: number, price: number, ma20: number): string => {
  if (price >= ma20) return 'text-green-500 dark:text-green-400';
  return 'text-red-500 dark:text-red-400';
};

const getPriceColorClass = (colorCode: number): string => {
  switch (colorCode) {
    case 0: return 'text-[#ff0000]';
    case 1: return 'text-[#ffa500]';
    case 2: return 'text-[#b2ff00]';
    case 3: return 'text-[#00ff00]';
    default: return 'text-gray-500';
  }
};

// Kode warna volume_color (backend) — sama persis dengan halaman Volume
const getVolumeColor = (comp: number): string => {
  switch (comp) {
    case 1: return 'text-[#00ff00]';
    case 2: return 'text-[#b2ff00]';
    case 3: return 'text-[#ff0000]';
    case 4: return 'text-[#ffa500]';
    default: return 'text-gray-300';
  }
};

// Warna % chg & Price: merah turun, hijau naik, netral datar — sama dgn menu Volume.
const getPercentChgColor = (chg: number): string => {
  if (chg > 0) return 'text-[#00ff00]';
  if (chg < 0) return 'text-[#ff0000]';
  return 'text-gray-300';
};

// Hover D0–D4: %C dan %H hari itu (dari acc_dist, d{i}_pc / d{i}_ph). Kartu memegang state sendiri, jadi hover
// tidak merender ulang tabel; hilang saat ada scroll.
type MoveTip = { sym: string; day: number; date: string; c: number; h: number; rect: DOMRect };
const MOVE_TIP_W = 214, MOVE_TIP_H = 84;
const fmtMovePct = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1).replace('.', ',')}%`;
const MoveTipLayer = ({ bind }: { bind: (set: (t: MoveTip | null) => void) => void }) => {
  const [tip, setTip] = useState<MoveTip | null>(null);
  useEffect(() => { bind(setTip); }, [bind]);
  useEffect(() => {
    if (!tip) return;
    const hide = () => setTip(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [tip]);
  if (!tip) return null;
  const { rect } = tip;
  const left = rect.right + 8 + MOVE_TIP_W > window.innerWidth ? rect.left - MOVE_TIP_W - 8 : rect.right + 8;
  const top = Math.max(8, Math.min(rect.top - 6, window.innerHeight - MOVE_TIP_H - 8));
  return (
    <div className="fixed z-50 pointer-events-none rounded-lg border border-white/15 bg-[#11151d]/95 shadow-xl backdrop-blur-sm p-2.5 text-[12px] text-gray-200"
         style={{ left, top, width: MOVE_TIP_W }}>
      <div className="font-bold text-white">{tip.sym}<span className="font-normal text-gray-400"> · D{tip.day}{tip.date ? ` · ${tip.date}` : ''}</span></div>
      <div className="mt-2 grid grid-cols-2 gap-1.5 text-center">
        <div className="rounded bg-white/5 py-1">
          <div className="text-[10px] text-gray-400">%C · close</div>
          <div className={`text-[14px] font-semibold tabular-nums ${getPercentChgColor(tip.c)}`}>{fmtMovePct(tip.c)}</div>
        </div>
        <div className="rounded bg-white/5 py-1">
          <div className="text-[10px] text-gray-400">%H · high</div>
          <div className={`text-[14px] font-semibold tabular-nums ${getPercentChgColor(tip.h)}`}>{fmtMovePct(tip.h)}</div>
        </div>
      </div>
    </div>
  );
};

// Streak spike: berapa hari beruntun D0..D4 dgn volume > 2× MA40 (rumus VSpike),
// dihitung mundur dari D0, maks 5 hari. Sumber volume.zip: {p}_vol_pct = vol/MA40.
const SPIKE_RATIO = 2;
const extractVolRatios = (rows: Record<string, unknown>[]): Record<string, number[]> => {
  const map: Record<string, number[]> = {};
  for (const item of rows) {
    const s = String(item['Ticker'] ?? item['Stock'] ?? '');
    if (!s) continue;
    map[s] = ['0', '1', '2', '3', '4'].map(d => Number(item[`d${d}_vol_pct`]) || 0);
  }
  return map;
};
const calcSpikeStreak = (ratios: number[] | undefined): number | undefined => {
  if (!ratios) return undefined;
  let n = 0;
  for (const r of ratios) {
    if (r > SPIKE_RATIO) n++;
    else break;
  }
  return n;
};

// Warna VSpike (dipakai juga oleh sel Volume & Value): rule % = 0 sama dengan
// halaman Volume — comp netral → tier ratio (>5 ijo tua, 2-5 lime, <2 abu).
// Warna sel kolom periode — sama persis dgn renderRow (untuk filter warna).
// The % of a period cell: net lots % (vol_pct) for stocks, net value % (val_pct) for IHSG. IHSG adds up the lots of
// every stock (Rp50 and Rp10.000 shares alike), so it is ranked and judged by value (2h_accdist Rank::Value).
const periodPct = (item: AccumulationData, pData: AccumulationData['periods'][string]): number =>
  item.symbol === 'IHSG' || item.symbol === 'IDX' ? pData.val_pct : pData.vol_pct;

// Number of a period cell in the current view: the net value, or in "Persen" the % above.
const periodShown = (item: AccumulationData, pData: AccumulationData['periods'][string], valueFilter: string): number =>
  valueFilter !== 'Percent' ? pData.val : periodPct(item, pData);

// The Acc Dist colour comes from the net VALUE in both views (as in 2h_accdist), so "Persen" shows the same colour as
// "Value" even when the net lots (vol%) have the other sign; the ±4% gate uses the same % the view shows.
const getPeriodColorClass = (item: AccumulationData, p: string, valueFilter: string): string => {
  const pData = item.periods[p];
  if (!pData) return 'text-gray-600';
  const shown = periodShown(item, pData, valueFilter);
  if (shown === 0 || pData.val === 0) return 'text-gray-600';
  return getTextColor(pData.val, pData.comp, pData.xl, pData.xc, periodPct(item, pData), pData.nsb, pData.nss);
};

const getVsColorClass = (row: AccumulationData, displayPercent: number): string => {
  const ratio = row.vs_ratio;
  const comp = row.vs_comp;
  if (ratio === undefined || comp === undefined) return getPriceColorClass(row.vol_color);
  if (comp === 0 && (row.percent1d === 0 || displayPercent === 0)) {
    if (ratio > 5) return 'text-[#00ff00]';
    if (ratio >= 2) return 'text-[#b2ff00]';
    return 'text-gray-300';
  }
  return getVolumeColor(comp);
};

const PERIOD_REF_RE = /^[cf]*[dw]\d+$/;

const parsePeriodReference = (raw: string): CrossRefCondition | null =>
  parseCrossColumnReference(raw, PERIOD_REF_RE);

// Presets (saved filter settings): column key → header name for its descriptions; period keys as D0/W1/CD0 like the
// header (without the foreign 'f'), since the dates under them change every day
const PRESET_LABELS: Record<string, string> = {
  symbol: 'Ticker', trend: 'Trend', momentum: 'Momentum', insider: 'Insider', vs: 'VSpike', percent1d: '%',
  price: 'Price', volume: 'Volume', value: 'Value', ai_pola: 'AI Summary', ai_p: 'Peluang',
};
const presetLabel = (key: string) => PRESET_LABELS[key] ?? (PERIOD_REF_RE.test(key) ? key.replace('f', '').toUpperCase() : key);
const PAGE_SIZES = [25, 50, 100, 200, 500];

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChangeRef.current(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

const getPeriods = (calcFilter: 'Daily' | 'Cumulative', isForeign: boolean) => {
  const cPrefix = calcFilter === 'Daily' ? '' : 'c';
  const fPrefix = isForeign ? 'f' : '';
  const fullPrefix = `${cPrefix}${fPrefix}`;

  const maxDays = 4;
  const maxWeeks = 4;

  const arr = [];
  for (let i = 0; i <= maxDays; i++) {
      arr.push(`${fullPrefix}d${i}`);
  }
  for (let i = 1; i <= maxWeeks; i++) {
      arr.push(`${fullPrefix}w${i}`);
  }
  return arr.reverse();
};

export function StoryAccumulationDistribution() {
  const [dataPrimary, setDataPrimary] = useState<AccumulationData[]>([]);
  const [dataForeign, setDataForeign] = useState<AccumulationData[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [stockInput, setStockInput] = useState('');
  const [selectedTickers, setSelectedTickers] = useState<string[]>([]);
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [brokerInput, setBrokerInput] = useState('');
  const [showBrokerSuggestions, setShowBrokerSuggestions] = useState(false);
  const dropdownBrokerRef = useRef<HTMLDivElement>(null);
  
  const [valueFilter, setValueFilter] = useState<'Value' | 'Percent'>('Value');
  const [percentDenom, setPercentDenom] = useState<'Default' | 'TVol' | 'TVal' | 'NFLot' | 'NFVal'>('Default');
  const [tableFilter, setTableFilter] = useState<'By TLot' | 'By FNVol'>('By TLot');
  const [calcFilter, setCalcFilter] = useState<'Daily' | 'Cumulative'>('Daily');
  const [invFilter, setInvFilter] = useState<'All' | 'Foreign' | 'Domestic'>('All');
  const [brokerFilter, setBrokerFilter] = useState<string>('All');
  const [brokerList, setBrokerList] = useState<string[]>([]);

  // Form states (buffers changes until "Show" is clicked)
  const [formSelectedTickers, setFormSelectedTickers] = useState<string[]>([]);
  const [formValueFilter, setFormValueFilter] = useState<'Value' | 'Percent'>('Value');
  const [formPercentDenom, setFormPercentDenom] = useState<'Default' | 'TVol' | 'TVal' | 'NFLot' | 'NFVal'>('Default');
  const [formTableFilter, setFormTableFilter] = useState<'By TLot' | 'By FNVol'>('By TLot');
  const [formCalcFilter, setFormCalcFilter] = useState<'Daily' | 'Cumulative'>('Daily');
  const [formInvFilter, setFormInvFilter] = useState<'All' | 'Foreign' | 'Domestic'>('All');
  const [formBrokerFilter, setFormBrokerFilter] = useState<string>('All');

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [fetchTrigger, setFetchTrigger] = useState(1);
  const [volRatioMap, setVolRatioMap] = useState<Record<string, number[]>>({});
  // %C / %H hover card (its setter lives in MoveTipLayer, so hovering does not re-render this component)
  const moveTipSet = useRef<((t: MoveTip | null) => void) | null>(null);
  const bindMoveTip = useCallback((set: (t: MoveTip | null) => void) => { moveTipSet.current = set; }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isRefetching, setIsRefetching] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  // D{i} as the column header shows it ("21 Oct"), for the %C / %H hover card; acc_dist_dates already holds "24 Oct"
  const headerDate = (i: number) => {
    const parts = (availableDates[i] ?? '').split('-');
    if (parts.length !== 3) return availableDates[i] ?? '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${parseInt(parts[2] ?? '', 10)} ${months[parseInt(parts[1] ?? '', 10) - 1] ?? ''}`;
  };

  // Tombol AI: hasil dihitung di server (backend/ai/accdist), di sini hanya ditampilkan.
  // Akses: admin/developer, email allowlist, dan member bootcamp — sama dengan canUseAi di backend/src/routes/accdist-ai.ts.
  // (AI Monitoring tetap hanya admin/developer.)
  const { profile } = useProfile();
  const { showToast } = useToast();
  const canUseAi = hasBootcampAccess(profile) || hasAdminPrivileges(profile?.role || '') || NEW_BROKER_PRIVILEGED_EMAILS.includes(profile?.email || '');
  const [aiOn, setAiOn] = useState(false);
  const [ai, setAi] = useState<AccDistAiResults | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiDetail, setAiDetail] = useState<string | null>(null);
  const aiFetchedAt = useRef(0);

  const toggleAi = async () => {
    if (!canUseAi) {
      showToast({ type: 'warning', title: 'Menu khusus bootcamp' });
      return;
    }
    const next = !aiOn;
    setAiOn(next);
    setCurrentPage(1); // AI filters/sort start or stop applying
    if (!next && sortConfig && (sortConfig.key === 'ai_pola' || sortConfig.key === 'ai_p')) setSortConfig(null);
    // (re)fetch on switch-on when nothing is loaded, the last attempt failed, or the results are older than 5 minutes
    if (next && !aiLoading && (!ai || Date.now() - aiFetchedAt.current > AI_REFRESH_MS)) {
      setAiLoading(true);
      setAiError(null);
      const res = await api.getAccDistAi();
      if (res.success && res.data) {
        setAi(res.data);
        aiFetchedAt.current = Date.now();
      } else setAiError(res.error || 'Gagal memuat hasil AI');
      setAiLoading(false);
    }
  };

  // Load broker list once — from raw/brokermetadata.parquet (same source as BrokerTransaction)
  // This is more accurate than /api/broker/list (broker_list.csv) which currently contains stale/incorrect codes.
  useEffect(() => {
    let active = true;
    fetchBrokerList().then(brokers => {
      if (!active) return;
      if (brokers && brokers.length > 0) {
        setBrokerList(brokers);
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Load data from API
  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      try {
        const isFirstLoad = dataPrimary.length === 0 && dataForeign.length === 0;
        if (isFirstLoad) {
          setLoading(true);
        } else {
          setIsRefetching(true);
        }
        setError(null);

        const primaryType = invFilter === 'Foreign' ? 'foreign' : (invFilter === 'Domestic' ? 'domestic' : 'all');
        const resDatesPromise = availableDates.length === 0 ? fetchAccumulationDistributionDates() : Promise.resolve(null);
        const isBrokerMode = brokerFilter !== 'All';
        const part1Promise = isBrokerMode
          ? fetchBrokerAccumulationDistributionData(brokerFilter, primaryType, 1)
          : fetchAccumulationDistributionData(primaryType, 1);
        const part2Promise = isBrokerMode
          ? fetchBrokerAccumulationDistributionData(brokerFilter, primaryType, 2)
          : fetchAccumulationDistributionData(primaryType, 2);

        // VSpike streak — ratio volume harian vs MA40 dari volume.zip (rumus VSpike), paralel
        const volType = primaryType as VolumeType;
        const volPart1Promise = isBrokerMode
          ? fetchBrokerVolumeData(brokerFilter, volType, 1)
          : fetchVolumeData(volType, 1);
        const volPart2Promise = isBrokerMode
          ? fetchBrokerVolumeData(brokerFilter, volType, 2)
          : fetchVolumeData(volType, 2);

        const [resPrimary, resDates] = await Promise.all([
          part1Promise,
          resDatesPromise
        ]);

        if (!active) return;

        const parsedData = convertBackendToFrontend(resPrimary.data.accumulationData || []);
        if (tableFilter === 'By TLot') {
          setDataPrimary(parsedData);
          setDataForeign([]); // Not needed
        } else {
          setDataForeign(parsedData);
          setDataPrimary([]); // Not needed
        }
        
        if (resDates && resDates.success && resDates.data) {
          if (Array.isArray(resDates.data)) {
            setAvailableDates(resDates.data);
          } else if (Array.isArray(resDates.data.dates)) {
            setAvailableDates(resDates.data.dates);
          }
        }

        // Stop skeleton loading once Part 1 is displayed
        setLoading(false);
        // Keep the blue bar animating by ensuring isRefetching is true
        setIsRefetching(true);

        // Fetch Part 2 in background and append
        part2Promise.then((resPart2) => {
          if (!active) return;
          if (resPart2.success && resPart2.data.accumulationData) {
            const parsedDataPart2 = convertBackendToFrontend(resPart2.data.accumulationData);
            if (tableFilter === 'By TLot') {
              setDataPrimary(prev => {
                const existing = new Set(prev.map(d => d.symbol));
                const newItems = parsedDataPart2.filter(d => !existing.has(d.symbol));
                return [...prev, ...newItems];
              });
            } else {
              setDataForeign(prev => {
                const existing = new Set(prev.map(d => d.symbol));
                const newItems = parsedDataPart2.filter(d => !existing.has(d.symbol));
                return [...prev, ...newItems];
              });
            }
          }
        }).catch((err) => {
          console.error('Error fetching Part 2 accumulation data:', err);
        }).finally(() => {
          if (active) {
            setIsRefetching(false);
          }
        });

        // VSpike: merge ratio D0..D4 (volume.zip) → map symbol → [d0..d4 vol/MA40]
        Promise.all([volPart1Promise, volPart2Promise]).then(([vol1, vol2]) => {
          if (!active) return;
          const map = extractVolRatios(vol1.data.accumulationData || []);
          Object.assign(map, extractVolRatios(vol2.data.accumulationData || []));
          if (map['IDX']) map['IHSG'] = map['IDX'];
          if (map['IHSG']) map['IDX'] = map['IHSG'];
          setVolRatioMap(map);
        }).catch(() => {});

      } catch (err: any) {
        if (!active) return;
        console.error('Error accumulation data:', err);
        setError(err.message || 'Failed to load data');
        setLoading(false);
        setIsRefetching(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [fetchTrigger]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStockSuggestions(false);
      }
      if (dropdownBrokerRef.current && !dropdownBrokerRef.current.contains(event.target as Node)) {
        setShowBrokerSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredBrokers = useMemo(() => {
    if (!brokerInput) return brokerList;
    const q = brokerInput.toLowerCase();
    return brokerList.filter(b => b.toLowerCase().includes(q));
  }, [brokerList, brokerInput]);

  const activeData = tableFilter === 'By FNVol' ? dataForeign : dataPrimary;

  const periodsTotal = useMemo(() => getPeriods(calcFilter, false), [calcFilter]);
  const periodsForeign = useMemo(() => getPeriods(calcFilter, true), [calcFilter]);

  // Get unique tickers for filter dropdown — only IHSG/IDX + 4-char
  const allTickers = useMemo(() => {
    return [...new Set(activeData.map(item => item.symbol).filter(s => s === 'IHSG' || s === 'IDX' || s.length === 4))].sort();
  }, [activeData]);

  const filteredTickers = useMemo(() => {
    return stockInput
      ? allTickers.filter(ticker => ticker.toLowerCase().includes(stockInput.toLowerCase()))
      : allTickers;
  }, [allTickers, stockInput]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const SortIndicator = ({ col }: { col: string }) => {
    if (!sortConfig || sortConfig.key !== col) return null;
    // non-breaking space: the arrow never wraps to a second line, which would make that header row taller than the
    // other table's and shift the floating AI panel's rows off the table's rows
    return <span className="text-[10px]">{sortConfig.direction === 'asc' ? '\u00a0↑' : '\u00a0↓'}</span>;
  };

  const filterAndSortData = (dataset: AccumulationData[], periods: string[]) => {
    // Only show IHSG/IDX + 4-char tickers (hide warrants/ETCs)
    let filteredBase = dataset.filter(item => item.symbol === 'IHSG' || item.symbol === 'IDX' || item.symbol.length === 4);
    const selectedSet = new Set(selectedTickers);
    let filtered = selectedTickers.length === 0
      ? filteredBase
      : filteredBase.filter(item => selectedSet.has(item.symbol));
      
    // Filter per kolom: ekspresi `A and B or C` + token warna (merah/oren/ijo/lime/netral/kuning)
    const is4SepData = availableDates[0]?.includes('4 Sep') ?? false;

    const pass = (expr: string | undefined, cell: FilterCell, customAtom?: AtomEvaluator): boolean => {
      if (!expr || !expr.trim()) return true;
      return customAtom
        ? evaluateFilterExpression(expr, cell, { customAtom })
        : evaluateFilterExpression(expr, cell);
    };

    // Apply column filters
    filtered = filtered.filter(item => {
      const isIdxRow = item.symbol === 'IHSG' || item.symbol === 'IDX';
      const displayPrice = (isIdxRow && is4SepData) ? 6636 : item.price;
      const displayPercent = (isIdxRow && is4SepData) ? -0.47 : item.percent1d;
      const priceColor = displayPrice === 0 ? 'text-gray-600' : getPercentChgColor(displayPercent);
      const volCellColor = (item.volume === 0 || item.price === 0) ? 'text-gray-600' : getVsColorClass(item, displayPercent);
      const vsStreak = calcSpikeStreak(volRatioMap[item.symbol]);
      const vsCellColor = vsStreak ? getVsColorClass(item, displayPercent) : 'text-gray-600';
      const valueCellColor = (item.value === 0 || item.price === 0) ? 'text-gray-600' : getVsColorClass(item, displayPercent);
      const trendColor = item.trend === 'Up Trend' ? 'text-[#00ff00]' : item.trend === 'Down Trend' ? 'text-[#ff0000]' : 'text-gray-500';
      const momentumStr = item.momentum === 2 ? 'up' : item.momentum === 0 ? 'down' : 'sw';
      const momentumColor = item.momentum === 2 ? 'text-[#00ff00]' : item.momentum === 0 ? 'text-[#ff0000]' : 'text-gray-500';
      const insiderColor = item.insider === undefined ? 'text-gray-600' : item.insider > 0 ? 'text-[#00ff00]' : item.insider < 0 ? 'text-[#ff0000]' : 'text-[#ffff00]';

      if (!pass(columnFilters.symbol, { value: NaN, text: item.symbol.toLowerCase(), colorClass: 'text-gray-300', isNumeric: false })) return false;
      if (!pass(columnFilters.trend, { value: NaN, text: item.trend.toLowerCase(), colorClass: trendColor, isNumeric: false })) return false;
      if (!pass(columnFilters.momentum, { value: NaN, text: momentumStr, colorClass: momentumColor, isNumeric: false })) return false;
      if (!pass(columnFilters.insider, { value: item.insider ?? 0, text: String(item.insider ?? 0), colorClass: insiderColor, isNumeric: true })) return false;
      if (!pass(columnFilters.percent1d, { value: item.percent1d, text: String(item.percent1d), colorClass: priceColor, isNumeric: true })) return false;
      if (!pass(columnFilters.price, { value: item.price, text: String(item.price), colorClass: priceColor, isNumeric: true })) return false;
      if (!pass(columnFilters.volume, { value: item.volume, text: String(item.volume), colorClass: volCellColor, isNumeric: true })) return false;
      if (!pass(columnFilters['vs'], { value: vsStreak ?? 0, text: String(vsStreak ?? 0), colorClass: vsCellColor, isNumeric: true })) return false;
      if (!pass(columnFilters.value, { value: item.value, text: String(item.value), colorClass: valueCellColor, isNumeric: true })) return false;
      if (aiOn && ai) {
        const s = ai.stocks[item.symbol];
        // kolom AI menampilkan pola → filter teks ("early", "konsisten akum" atau "konsisten akumulasi") dan token warna
        const polaText = s ? `${aiShapeName(ai, s)} ${aiShapeShort(aiShapeName(ai, s))}`.toLowerCase() : '';
        if (!pass(columnFilters['ai_pola'], { value: NaN, text: polaText, colorClass: s ? aiShapeFilterColor(s.tl) : 'text-gray-600', isNumeric: false })) return false;
        // the same whole-number percent the cell shows, so ">=22" matches every cell that reads 22%
        if (!pass(columnFilters['ai_p'], { value: s ? Math.round(s.p * 100) : NaN, text: s ? String(Math.round(s.p * 100)) : '', colorClass: s ? aiProbColor(s.p, ai.base_rate) : 'text-gray-600', isNumeric: true })) return false;
      }

      // the number each period cell shows in this view (IHSG: value % in "Persen", see periodShown)
      const shownOf = (name: string) => {
        const d = item.periods[name];
        return d ? periodShown(item, d, valueFilter) : 0;
      };
      for (const p of periods) {
        const expr = columnFilters[p];
        if (!expr || !expr.trim()) continue;
        const pVal = shownOf(p);
        const cell: FilterCell = { value: pVal, text: String(pVal), colorClass: getPeriodColorClass(item, p, valueFilter), isNumeric: true };
        const periodRefAtom: AtomEvaluator = (atom, c) => {
          const ref = parsePeriodReference(atom);
          if (!ref) return null;
          return evaluateCrossColumn(c.value, shownOf(ref.ref), ref);
        };
        // other period columns by name inside abs(...) / |...| (e.g. ">|d1|", "|d0|>|d1|")
        const resolveRef = (name: string) => (PERIOD_REF_RE.test(name) ? shownOf(name) : undefined);
        if (!evaluateFilterExpression(expr, cell, { customAtom: periodRefAtom, resolveRef })) return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (!sortConfig) return 0;
      let aValue: any;
      let bValue: any;
      
      if (sortConfig.key === 'symbol') {
        aValue = a.symbol;
        bValue = b.symbol;
      } else if (sortConfig.key === 'price') {
        aValue = a.price;
        bValue = b.price;
      } else if (sortConfig.key === 'percent1d') {
        aValue = a.percent1d;
        bValue = b.percent1d;
      } else if (sortConfig.key === 'volume') {
        aValue = a.volume;
        bValue = b.volume;
      } else if (sortConfig.key === 'vs') {
        aValue = calcSpikeStreak(volRatioMap[a.symbol]) ?? -1;
        bValue = calcSpikeStreak(volRatioMap[b.symbol]) ?? -1;
      } else if (sortConfig.key === 'value') {
        aValue = a.value;
        bValue = b.value;
      } else if (sortConfig.key === 'trend') {
        aValue = a.trend;
        bValue = b.trend;
      } else if (sortConfig.key === 'momentum') {
        aValue = a.momentum;
        bValue = b.momentum;
      } else if (sortConfig.key === 'insider') {
        const av = a.insider;
        const bv = b.insider;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        aValue = av;
        bValue = bv;
      } else if (sortConfig.key === 'ai_pola' || sortConfig.key === 'ai_p') {
        // AI: tanpa hasil selalu di bawah; pola diurutkan (akumulasi → distribusi) lalu probabilitas
        const sa = ai?.stocks[a.symbol];
        const sb = ai?.stocks[b.symbol];
        if (!sa && !sb) return 0;
        if (!sa) return 1;
        if (!sb) return -1;
        // equal probabilities (same calibration step) are ordered by the model's within-day rank
        aValue = (sortConfig.key === 'ai_pola' ? aiShapeRank(sa) * 10 : 0) + sa.p + (sa.rk ?? 0) * 1e-4;
        bValue = (sortConfig.key === 'ai_pola' ? aiShapeRank(sb) * 10 : 0) + sb.p + (sb.rk ?? 0) * 1e-4;
      } else {
        const pa = a.periods[sortConfig.key], pb = b.periods[sortConfig.key];
        aValue = pa ? periodShown(a, pa, valueFilter) : 0;
        bValue = pb ? periodShown(b, pb, valueFilter) : 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const table1Ref = useRef<HTMLDivElement>(null);
  const table2Ref = useRef<HTMLDivElement>(null);

  const handleScroll = (sourceIndex: number, e: React.UIEvent<HTMLDivElement>) => {
    const targetRef = sourceIndex === 0 ? table2Ref : table1Ref;
    if (targetRef.current) {
      if (targetRef.current.scrollTop !== e.currentTarget.scrollTop) {
        targetRef.current.scrollTop = e.currentTarget.scrollTop;
      }
      if (targetRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
        targetRef.current.scrollLeft = e.currentTarget.scrollLeft;
      }
    }
  };

  // Row highlight shared by the table and the floating AI panel (rows with the same data-sym): one CSS rule, so
  // moving the mouse never re-renders the table
  const hoverStyleRef = useRef<HTMLStyleElement>(null);
  const hoverSymRef = useRef('');
  const setRowHover = (sym: string) => {
    if (sym === hoverSymRef.current || !hoverStyleRef.current) return;
    hoverSymRef.current = sym;
    hoverStyleRef.current.textContent = sym ? `tr[data-sym="${CSS.escape(sym)}"]{background-color:rgba(255,255,255,0.25)}` : '';
  };
  const syncRowHover = (e: React.MouseEvent) => setRowHover((e.target as HTMLElement).closest('tr')?.getAttribute('data-sym') ?? '');
  const clearRowHover = () => setRowHover('');
  // The floating AI panel scrolls its own rows; table and panel follow each other's vertical position. Neither may
  // go further than the other can follow.
  const aiPanelRef = useRef<HTMLDivElement>(null);
  const syncScrollTop = (from: HTMLDivElement, to: HTMLDivElement | null) => {
    if (!to) return;
    const top = Math.min(from.scrollTop, to.scrollHeight - to.clientHeight);
    if (Math.abs(to.scrollTop - top) > 0.5) to.scrollTop = top;
    if (from.scrollTop > top + 0.5) from.scrollTop = top;
  };
  // The panel is as tall as the table's visible area (its horizontal scrollbar excluded) and shrinks to its rows when
  // they all fit: no empty space inside it, and both reach their last row together.
  const [aiPanelMax, setAiPanelMax] = useState(0);
  const mainSizeObserver = useRef<ResizeObserver | null>(null);
  const watchMainTable = useCallback((el: HTMLDivElement | null) => {
    mainSizeObserver.current?.disconnect();
    mainSizeObserver.current = null;
    if (!el) return;
    const update = () => setAiPanelMax(el.clientHeight);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);  // a wider table brings a horizontal scrollbar
    mainSizeObserver.current = ro;
    update();
  }, []);
  const mainRef0 = useCallback((el: HTMLDivElement | null) => {
    (table1Ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
    watchMainTable(el);
  }, [watchMainTable]);
  const mainRef1 = useCallback((el: HTMLDivElement | null) => {
    (table2Ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
    watchMainTable(el);
  }, [watchMainTable]);

  const sortedDataTotal = useMemo(() => {
    return filterAndSortData(dataPrimary, periodsTotal);
  }, [dataPrimary, periodsTotal, selectedTickers, columnFilters, sortConfig, valueFilter, volRatioMap, ai, aiOn]);

  const sortedDataForeign = useMemo(() => {
    return filterAndSortData(dataForeign, periodsForeign);
  }, [dataForeign, periodsForeign, selectedTickers, columnFilters, sortConfig, valueFilter, volRatioMap, ai, aiOn]);

  // Filter berubah → kembali ke halaman 1 (sinkron dengan menu Volume)
  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters]);

  // Pagination calculations
  const renderTable = (sortedData: AccumulationData[], periods: string[], isForeign: boolean, tableIndex: number) => {
    const idxRow = sortedData.find(r => r.symbol === 'IHSG' || r.symbol === 'IDX');
    const remainingData = sortedData.filter(r => r.symbol !== 'IHSG' && r.symbol !== 'IDX');
    const totalPages = Math.ceil(remainingData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = remainingData.slice(startIndex, endIndex);

    const is4SepData = availableDates[0]?.includes('4 Sep');
    const renderRow = (row: AccumulationData, isIdx: boolean = false) => {
      const isIHSG4Sep = isIdx && is4SepData;
      const displayPrice = isIHSG4Sep ? 6636 : row.price;
      const displayPercent = isIHSG4Sep ? -0.47 : row.percent1d;
      const vsStreak = calcSpikeStreak(volRatioMap[row.symbol]);
      return (
      <tr key={row.symbol} data-sym={row.symbol} className={`hover:bg-white/[0.25] transition-colors h-[18px] divide-x divide-white/30 ${isIdx ? 'bg-blue-900/30' : ''}`}>
        <td className="px-1 py-0 text-left text-gray-300 truncate">{row.symbol === 'IDX' ? 'IHSG' : row.symbol}</td>
        <td className={`px-1 py-0 text-center truncate ${row.trend === 'Up Trend' ? 'text-[#00ff00]' : row.trend === 'Down Trend' ? 'text-[#ff0000]' : 'text-gray-500'}`}>{row.trend === 'Up Trend' ? 'UP' : row.trend === 'Down Trend' ? 'DOWN' : 'SW'}</td>
        <td className={`px-1 py-0 text-center truncate ${row.momentum === 2 ? 'text-[#00ff00]' : row.momentum === 0 ? 'text-[#ff0000]' : 'text-gray-500'}`}>{row.momentum === 2 ? 'UP' : row.momentum === 0 ? 'DOWN' : 'SW'}</td>
        <td className={`px-1 py-0 text-right truncate ${row.insider === undefined ? 'text-gray-600' : row.insider > 0 ? 'text-[#00ff00]' : row.insider < 0 ? 'text-[#ff0000]' : 'text-[#ffff00]'}`}>
          {row.insider === undefined ? '-' : formatNumberWithAbbreviation(row.insider)}
        </td>
        <td className={`px-1 py-0 text-right truncate ${vsStreak ? getVsColorClass(row, displayPercent) : 'text-gray-600'}`}>
          {vsStreak ? `${vsStreak}X` : '-'}
        </td>
        {periods.map((p) => {
          const pData = row.periods[p];
          if (!pData) return <td key={p} className="px-1 py-0 text-right text-gray-600">-</td>;
          const val = periodShown(row, pData, valueFilter);
          const colorClass = getPeriodColorClass(row, p, valueFilter);
          const day = p.match(/^f?d(\d)$/)?.[1];  // daily columns only (not the cumulative cd*/cfd*)
          const mv = day !== undefined ? row.moves?.[`d${day}`] : undefined;
          const hover = mv && day !== undefined ? {
            onMouseEnter: (e: React.MouseEvent<HTMLTableCellElement>) => moveTipSet.current?.({
              sym: row.symbol === 'IDX' ? 'IHSG' : row.symbol, day: Number(day), date: headerDate(Number(day)), c: mv.c, h: mv.h,
              rect: e.currentTarget.getBoundingClientRect(),
            }),
            onMouseLeave: () => moveTipSet.current?.(null),
          } : {};
          return (
            <td key={p} className={`px-1 py-0 text-right truncate ${colorClass}`} {...hover}>
              {val === 0 ? '-' : (valueFilter === 'Value' ? formatNumberWithAbbreviation(val) : val.toFixed(1) + '%')}
            </td>
          );
        })}
        <td className={`px-1 py-0 text-right truncate ${displayPrice === 0 ? 'text-gray-600' : getPercentChgColor(displayPercent)}`}>
           {displayPrice === 0 ? '-' : displayPercent.toFixed(1) + '%'}
         </td>
         <td className={`px-1 py-0 text-right truncate ${displayPrice === 0 ? 'text-gray-600' : getPercentChgColor(displayPercent)}`}>
            {displayPrice === 0 ? '-' : new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(displayPrice)}
          </td>
        <td className={`px-1 py-0 text-right truncate ${row.volume === 0 || row.price === 0 ? 'text-gray-600' : getVsColorClass(row, displayPercent)}`}>
          {row.volume === 0 || row.price === 0 ? '-' : formatNumberWithAbbreviation(row.volume)}
        </td>
        {/* with the AI panel on, the table ends here: draw its right edge (the panel is a separate card) */}
        <td className={`px-1 py-0 text-right truncate ${aiOn ? 'border-r border-white/30' : ''} ${row.value === 0 || row.price === 0 ? 'text-gray-600' : getVsColorClass(row, displayPercent)}`}>
          {row.value === 0 || row.price === 0 ? '-' : formatNumberWithAbbreviation(row.value)}
        </td>
      </tr>
      );
    };

    // Row of the floating AI panel (Summary + Peluang naik): same order and height as the table's row of the same stock
    const renderAiRow = (row: AccumulationData, isIdx: boolean = false) => {
      const s = isIdx ? undefined : ai?.stocks[row.symbol];
      const skip = isIdx ? undefined : ai?.skipped?.[row.symbol];
      let cells: React.ReactNode;
      if (!s && skip) {
        cells = (
          <>
            <td className="px-1 py-0 text-left text-gray-600" title={`Tidak dinilai: ${skip}`}>n/a</td>
            <td className="px-1 py-0 text-right text-gray-600 cursor-pointer hover:underline" title={`Tidak dinilai: ${skip}`} onClick={() => setAiDetail(row.symbol)}>n/a</td>
          </>
        );
      } else if (!s) {
        cells = (
          <>
            <td className="px-1 py-0 text-left text-gray-600">{aiLoading && !isIdx ? '…' : '-'}</td>
            <td className="px-1 py-0 text-right text-gray-600">{aiLoading && !isIdx ? '…' : '-'}</td>
          </>
        );
      } else {
        cells = (
          <>
            <td className="px-1 py-0 text-left truncate" style={{ color: aiShapeColor(s.tl) }}
                title={aiShapeName(ai, s)}>
              {s.tl ? aiShapeShort(aiShapeName(ai, s)) : aiLevelName(ai, aiLevel(s))}
            </td>
            <td className={`px-1 py-0 text-right truncate cursor-pointer hover:underline ${aiProbColor(s.p, ai?.base_rate ?? 0.22)}`}
                title={ai && hasStrongWarning(s, ai.patterns) ? 'Ada pola waspada kuat — klik untuk penjelasan' : 'Klik untuk penjelasan'}
                onClick={() => setAiDetail(row.symbol)}>
              {Math.round(s.p * 100)}%{ai && hasStrongWarning(s, ai.patterns) ? '!' : ''}
            </td>
          </>
        );
      }
      return (
        <tr key={row.symbol} data-sym={row.symbol} className={`hover:bg-white/[0.25] transition-colors h-[18px] divide-x divide-white/30 ${isIdx ? 'bg-blue-900/30' : ''}`}>
          {cells}
        </tr>
      );
    };

     return (
      <div className="flex flex-col bg-black flex-auto min-h-0 w-full border-t border-white/30">
        <style ref={hoverStyleRef} />
        <div className="flex flex-auto min-h-0" onMouseOver={syncRowHover} onMouseLeave={clearRowHover}>
        <div
          className="flex-1 min-w-0 overflow-y-auto overflow-x-auto"
          style={{ willChange: 'transform' }}
          ref={tableIndex === 0 ? mainRef0 : mainRef1}
          onScroll={(e) => { handleScroll(tableIndex, e); syncScrollTop(e.currentTarget, aiPanelRef.current); }}
        >
          <table className="w-full text-[12px] tabular-nums table-fixed min-w-[600px] leading-tight border-separate border-spacing-0">
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10" style={{ transform: 'translateZ(0)' }}>
              <tr className="cursor-pointer select-none h-[22px] whitespace-nowrap divide-x divide-white/30">
                <th onClick={() => handleSort('symbol')} className="px-1 py-0 text-center w-[60px]">Ticker<SortIndicator col="symbol" /></th>
                <th onClick={() => handleSort('trend')} className="px-1 py-0 text-center w-[60px]">Trend<SortIndicator col="trend" /></th>
                <th onClick={() => handleSort('momentum')} className="px-1 py-0 text-center w-[60px]">Momentum<SortIndicator col="momentum" /></th>
                <th onClick={() => handleSort('insider')} className="px-1 py-0 text-center w-[60px]" title="5 hari terakhir">Insider<SortIndicator col="insider" /></th>
                <th onClick={() => handleSort('vs')} className="px-1 py-0 text-center w-[60px]" title="Hari spike >2X beruntun dari D0 (maks 5 hari)">VSpike<SortIndicator col="vs" /></th>
                {periods.map((p) => {
                  let rawLabel = isForeign ? p.replace('f', '').toUpperCase() : p.toUpperCase();
                  let label = rawLabel;
                  let thTitle = rawLabel;
                  
                  // Handle D0..D5 and CD0..CD5
                  const dMatch = rawLabel.match(/^(?:C)?D(\d+)$/);
                  if (dMatch && availableDates.length > 0) {
                     const dIndex = parseInt(dMatch[1]);
                     if (!isNaN(dIndex) && dIndex < availableDates.length) {
                        const dateStr = availableDates[dIndex];
                        if (dateStr) {
                           const parts = dateStr.split('-');
                           if (parts.length === 3) {
                             const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                             const day = parseInt(parts[2], 10);
                             const monthStr = months[parseInt(parts[1], 10) - 1];
                             label = `${day} ${monthStr}`;
                             thTitle = `${rawLabel} — ${day} ${monthStr} ${parts[0]}`;
                           } else {
                             label = dateStr;
                             thTitle = `${rawLabel} — ${dateStr}`;
                           }
                        }
                     }
                  }

                  let sectionBg = '';
                  if (rawLabel === 'D0' || rawLabel === 'CD0') sectionBg = 'bg-emerald-500/20';
                  else if (rawLabel.startsWith('D') || rawLabel.startsWith('CD')) sectionBg = 'bg-blue-500/20';
                  else if (rawLabel.startsWith('W') || rawLabel.startsWith('CW')) sectionBg = 'bg-purple-500/20';
                  return <th key={p} title={thTitle} onClick={() => handleSort(p)} className={`px-1 py-0 text-center w-[60px] ${sectionBg}`}>{label}<SortIndicator col={p} /></th>;
                })}
                <th onClick={() => handleSort('percent1d')} className="px-1 py-0 text-center w-[60px] bg-emerald-500/20">%<SortIndicator col="percent1d" /></th>
                <th onClick={() => handleSort('price')} className="px-1 py-0 text-center w-[70px] bg-emerald-500/20">Price<SortIndicator col="price" /></th>
                <th onClick={() => handleSort('volume')} className="px-1 py-0 text-center w-[70px] bg-emerald-500/20">Volume<SortIndicator col="volume" /></th>
                <th onClick={() => handleSort('value')} className={`px-1 py-0 text-center w-[70px] bg-emerald-500/20 ${aiOn ? 'border-r border-white/30' : ''}`}>Value<SortIndicator col="value" /></th>
              </tr>
              {/* Interactive filter row */}
              <tr className="bg-gray-800/80 backdrop-blur-md h-[22px] divide-x divide-white/30">
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-left text-white placeholder:text-gray-500 focus:outline-none" placeholder="Filter" value={columnFilters.symbol || ''} onChange={(val) => setColumnFilters(prev => ({...prev, symbol: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none" placeholder="UP" value={columnFilters.trend || ''} onChange={(val) => setColumnFilters(prev => ({...prev, trend: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none" placeholder="UP" value={columnFilters.momentum || ''} onChange={(val) => setColumnFilters(prev => ({...prev, momentum: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none" placeholder=">1B" value={columnFilters.insider || ''} onChange={(val) => setColumnFilters(prev => ({...prev, insider: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">=2" value={columnFilters['vs'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, vs: val}))} />
                </td>
                {periods.map((p) => (
                  <td key={p} className="px-1 py-0">
                    <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">1k" title=">1B, >D1, >D1+5B, >(D1/2) · nilai absolut: |x|>1B (atau abs>1B); <|D0| = |sel| < |D0| (x<|D0| = sel bertanda)" value={columnFilters[p] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, [p]: val}))} />
                  </td>
                ))}
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">5" value={columnFilters.percent1d || ''} onChange={(val) => setColumnFilters(prev => ({...prev, percent1d: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder="<200" value={columnFilters.price || ''} onChange={(val) => setColumnFilters(prev => ({...prev, price: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">5" value={columnFilters.volume || ''} onChange={(val) => setColumnFilters(prev => ({...prev, volume: val}))} />
                </td>
                <td className={`px-1 py-0 ${aiOn ? 'border-r border-white/30' : ''}`}>
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">1B" value={columnFilters.value || ''} onChange={(val) => setColumnFilters(prev => ({...prev, value: val}))} />
                </td>
              </tr>
            </thead>
            <tbody className="divide-y-0">
              {idxRow && renderRow(idxRow, true)}
              {paginatedData.map((row) => renderRow(row))}
            </tbody>
          </table>
          {/* same 6px as below the AI panel's rows, so its last row clears the rounded bottom edge */}
          {aiOn && <div aria-hidden="true" className="h-1.5" />}
        </div>
          {aiOn && (
            // Floating AI panel (like the AI panel in Broker Summary): a card that stays put next to the table while
            // its rows scroll inside it, kept at the table's scroll position (same header and row heights), so the
            // rounded frame never moves and the panel stays in view when the table is scrolled sideways.
            // Ring/shadow instead of a border so nothing shifts by 1px.
            <div className="shrink-0 self-start ml-5 mr-4 flex flex-col overflow-hidden rounded-xl bg-[#0d0a1c] ring-1 ring-fuchsia-400/60 shadow-[0_0_16px_rgba(217,70,239,0.35)]"
                 style={aiPanelMax ? { maxHeight: aiPanelMax } : undefined}>
              <div ref={aiPanelRef} onScroll={(e) => syncScrollTop(e.currentTarget, tableIndex === 0 ? table1Ref.current : table2Ref.current)}
                   className="min-h-0 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {/* Header rows stay one line (22px) in both tables so the panel's rows line up with the table's;
                  Peluang is wide enough for "Peluang ↓" */}
              <table className="text-[12px] tabular-nums table-fixed leading-tight border-separate border-spacing-0" style={{ width: 240 }}>
                <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10" style={{ transform: 'translateZ(0)' }}>
                  <tr className="cursor-pointer select-none h-[22px] whitespace-nowrap divide-x divide-white/30">
                    <th onClick={() => handleSort('ai_pola')} className={`px-1 py-0 text-center w-[168px] bg-fuchsia-500/25 ${aiDateOk ? '' : 'text-[#ffa500]'}`} title={aiHeaderTitle}>
                      <span className="mr-1 rounded-sm bg-fuchsia-500/70 px-1 text-[10px] text-white">AI</span>Summary<SortIndicator col="ai_pola" />
                    </th>
                    <th onClick={() => handleSort('ai_p')} className={`px-1 py-0 text-center w-[72px] bg-fuchsia-500/25 ${ai?.market_warning ? 'text-[#ffa500]' : ''}`} title={probHeaderTitle}>Peluang<SortIndicator col="ai_p" /></th>
                  </tr>
                  <tr className="bg-gray-800/80 backdrop-blur-md h-[22px] divide-x divide-white/30">
                    <td className="px-1 py-0">
                      <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-left text-white placeholder:text-gray-500 focus:outline-none" placeholder="early" value={columnFilters['ai_pola'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, ai_pola: val}))} />
                    </td>
                    <td className="px-1 py-0">
                      <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none" placeholder=">30" value={columnFilters['ai_p'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, ai_p: val}))} />
                    </td>
                  </tr>
                </thead>
                <tbody className="divide-y-0">
                  {idxRow && renderAiRow(idxRow, true)}
                  {paginatedData.map((row) => renderAiRow(row))}
                </tbody>
              </table>
              <div aria-hidden="true" className="h-1.5" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Judul tabel hanya menampilkan error AI; tanggal, arti kolom dan peringatan pasar ada di tooltip header Summary/Peluang
  const accD0 = availableDates[0] || '';
  const aiDateOk = !ai || sameAccDistDate(ai.date, accD0);
  const aiStatus = aiOn && aiError ? <span className="ml-3 font-normal text-[#ffa500]">AI: {aiError}</span> : null;
  const polaText = 'pola Acc Dist dari Value kolom W4–D0: Konsisten Akumulasi Kuat/Melemah, Early Akumulasi, Netral, Early Distribusi, Konsisten Distribusi Melemah/Kuat';
  const aiHeaderTitle = ai
    ? `Summary ${fmtYmd(ai.date)} — ${polaText}${aiDateOk ? '' : ` (data AI belum diperbarui ke D0 ${accD0})`}`
    : `Summary — ${polaText}`;
  // The Peluang column is the calibrated Peluang naik (p), not the AI Prob (kp, shown in the dialog's detail grid)
  const probHeaderTitle = `Peluang naik — ${ai?.target ?? 'Prediksi: naik di atas 5% dalam 3 hari bursa'}`
    + (ai?.method === 'avg'
      ? '. Volatilitas 20 hari, rentang harian, harga makin murah, turun hari ini dan Rp per 1% asing diurutkan di antara '
        + 'semua saham hari itu, dirata-rata, lalu dicocokkan dengan hasil nyata ±6 bulan terakhir per grup likuiditas'
      : '')
    + ' · ! = ada pola waspada kuat · klik angka untuk penjelasan'
    + (ai?.market_warning ? ` · ${ai.market_warning}` : '');

  // Prob from the stock's own averages: each parameter today vs the stock's own normal (Ko Nic), and what actually
  // happened historically for this Prob level
  // AI Prob per parameter (6 × 9 grid), folded away by default; the AI Prob itself is in its title (the card at the top
  // is the Peluang naik)
  const ownBlock = (s: AiStock) => {
    if (s.kp == null || !ai?.own || !s.kq) return null;
    const own = ai.own;
    const fmt = (key: string, v: number | null | undefined) => {
      if (v == null) return '–';
      if (key === 'value') return `${v < 0 ? '−' : ''}Rp${Math.abs(v) >= 1000 ? `${(Math.abs(v) / 1000).toFixed(1).replace('.', ',')} M` : `${Math.round(Math.abs(v))} jt`}`;
      if (key === 'vol' || key === 'persen' || key === 'rp1') return `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(1).replace('.', ',')}%`;
      if (key === 'xlxc') return `${v.toFixed(1).replace('.', ',').replace(',0', '')} jual`;
      return `${v < 0 ? '−' : ''}${Math.abs(Math.round(v * 100))}%`;  // broker: (beli − jual) / total; non split: porsi pembeli
    };
    const periods = own.periods ?? ['W4', 'W3', 'W2', 'W1', 'D4', 'D3', 'D2', 'D1', 'D0'];
    const weights = own.weights ?? [0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
    const cellBg = (q: number) =>
      q >= 0.8 ? 'rgba(0,255,0,.28)' : q >= 0.6 ? 'rgba(178,255,0,.22)' : q <= 0.2 ? 'rgba(255,0,0,.28)' : q <= 0.4 ? 'rgba(255,165,0,.25)' : 'rgba(156,163,175,.14)';
    const compat = own.compat;
    const cols = `minmax(96px,1fr) 64px 64px ${periods.map(p => (p === 'D0' ? '38px' : '26px')).join(' ')} 40px${compat ? ' 40px' : ''}`;
    return (
      <details className="group rounded-lg border border-border bg-white/[0.02]">
        <summary className="cursor-pointer select-none list-none px-3 py-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
          <span>Detail AI Prob {Math.round(s.kp * 100)}% · {own.params.length} parameter × {periods.length} kolom</span>
          <span className="transition-transform group-open:rotate-180">▾</span>
        </summary>
        <div className="px-3 pb-3 space-y-2">
        <div className="text-xs text-muted-foreground">
          Tiap kotak = posisi hari / minggu itu dibanding kebiasaan {aiDetail} sendiri (D: {own.window} hari, W: {own.week_window ?? 26} minggu).
          {' '}50 = biasa, 100 = jauh di atas. Angka kecil di judul kolom = bobotnya.
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[580px] space-y-0.5">
            <div className="grid gap-1 text-[10px] text-muted-foreground uppercase tracking-wide items-end" style={{ gridTemplateColumns: cols }}>
              <span>Parameter</span><span className="text-right normal-case">{d0Label}</span><span className="text-right">Rata-rata</span>
              {periods.map((p, j) => (
                <span key={p} className="text-center leading-tight normal-case">{colLabel(p)}<br /><span>{Math.round((weights[j] ?? 0) * 100)}</span></span>
              ))}
              <span className="text-right" title="Posisi parameter ini, rata-rata 9 kolom dengan bobot D0 100% → W4 20%">Posisi</span>
              {compat && <span className="text-right" title="Bobot parameter di AI Prob, dari kecocokannya dengan target">Bobot</span>}
            </div>
            {own.params.map((p, i) => {
              const q = s.kq?.[i];
              const ci = compat ? compat.keys.indexOf(p.key) : -1;
              const w = ci >= 0 ? compat!.w[ci] ?? 0 : null;
              const auc = ci >= 0 ? compat!.auc[ci] ?? 0.5 : null;
              return (
                <div key={p.key} className={`grid gap-1 items-center ${w === 0 ? 'opacity-50' : ''}`} style={{ gridTemplateColumns: cols }}>
                  <span className="truncate">{p.name}</span>
                  <span className="text-right tabular-nums text-xs">{fmt(p.key, s.kx?.[i])}</span>
                  <span className="text-right tabular-nums text-xs text-muted-foreground">{fmt(p.key, s.km?.[i])}</span>
                  {periods.map((per, j) => {
                    const c = s.kg?.[j]?.[i];
                    return (
                      <span key={per} className="h-5 rounded-sm text-center text-[10px] leading-5 tabular-nums"
                            style={{ background: c != null ? cellBg(c) : 'transparent' }} title={`${p.name} ${per}: ${c != null ? `${Math.round(c * 100)}%` : 'tidak ada data'}`}>
                        {c != null ? Math.round(c * 100) : '–'}
                      </span>
                    );
                  })}
                  <span className={`text-right tabular-nums font-semibold ${q != null ? aiKpColor(q) : 'text-muted-foreground'}`}>{q != null ? `${Math.round(q * 100)}%` : '–'}</span>
                  {w != null && auc != null && (
                    <span className={`text-right tabular-nums ${w > 0 ? 'text-white' : 'text-muted-foreground'}`}
                          title={`Kecocokan dengan target: ${(auc * 100).toFixed(1).replace('.', ',')}% (50% = tidak nyambung, makin di atas 50% makin nyambung)${w === 0 ? ' — tidak dipakai' : ''}`}>
                      {Math.round(w * 100)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        </div>
      </details>
    );
  };

  const pct1 = (x: number) => `${(x * 100).toFixed(1).replace('.', ',')}%`;
  // D0 is written as the AI's date ("23 Okt") in the Summary chart and the AI Prob detail grid
  const d0Label = ai?.date ? fmtYmd(ai.date).replace(/ \d{4}$/, '') : 'D0';
  const colLabel = (c: string) => (c === 'D0' ? d0Label : c);

  // D10..D0 chart: each day's Acc Dist % (menu % view) and price change %, on their own zero-aligned scales (Acc Dist %
  // runs to tens of percent, price moves to a few). Daily only: the W columns overlap the D columns (W1 holds D1..D4).
  const DAY_NAMES = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
  const ACC_COLOR = '#60a5fa', CHG_COLOR = '#f59e0b';
  const dailyChart = (s: AiStock) => {
    const dates = ai?.daily?.dates;
    if (!dates || !s.dv || !s.dc || s.dv.length !== dates.length || s.dc.length !== dates.length) return null;
    const dv = s.dv, dc = s.dc, n = dates.length;
    const W = 480, H = 136, padL = 36, padR = 36, top = 8, bottom = H - 18, mid = (top + bottom) / 2, half = (bottom - top) / 2;
    const absMax = (a: (number | null)[], floor: number) => Math.max(floor, ...a.map(v => (v == null ? 0 : Math.abs(v))));
    const mA = absMax(dv, 1), mC = absMax(dc, 0.5);
    const x = (i: number) => padL + (i * (W - padL - padR)) / (n - 1);
    const yA = (v: number) => mid - (v / mA) * half;
    const yC = (v: number) => mid - (v / mC) * half;
    // a line broken where the stock did not trade
    const segments = (a: (number | null)[], y: (v: number) => number) => a.reduce<string[][]>((acc, v, i) => {
      if (v == null) { acc.push([]); return acc; }
      acc[acc.length - 1]!.push(`${x(i)},${y(v)}`);
      return acc;
    }, [[]]).filter(seg => seg.length > 1);
    // how well the two move together over these days (Pearson, days with both values)
    const pairs = dv.map((v, i) => [v, dc[i]] as const).filter((p): p is readonly [number, number] => p[0] != null && p[1] != null);
    const corr = (() => {
      if (pairs.length < 4) return null;
      const ma = pairs.reduce((t, p) => t + p[0], 0) / pairs.length, mc = pairs.reduce((t, p) => t + p[1], 0) / pairs.length;
      const cov = pairs.reduce((t, p) => t + (p[0] - ma) * (p[1] - mc), 0);
      const va = pairs.reduce((t, p) => t + (p[0] - ma) ** 2, 0), vc = pairs.reduce((t, p) => t + (p[1] - mc) ** 2, 0);
      return va > 0 && vc > 0 ? cov / Math.sqrt(va * vc) : null;
    })();
    const fmt = (v: number | null | undefined) => (v == null ? 'tidak trading' : fmtMovePct(v));
    const dayLabel = (d: string, i: number) => (i === n - 1 ? fmtYmd(d).replace(/ \d{4}$/, '') : String(parseInt(d.slice(6), 10)));
    const dayName = (d: string) => DAY_NAMES[new Date(+d.slice(0, 4), +d.slice(4, 6) - 1, +d.slice(6)).getDay()] ?? '';
    return (
      <div className="mt-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: ACC_COLOR }} />Acc Dist %</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: CHG_COLOR }} />Chg%</span>
          {corr != null && <span className="ml-auto tabular-nums" title="Korelasi Acc Dist % dengan Chg% di hari-hari ini (−1 berlawanan, 0 tidak berhubungan, +1 searah)">korelasi {corr >= 0 ? '+' : '−'}{Math.abs(corr).toFixed(2).replace('.', ',')}</span>}
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto mt-1" role="img"
             aria-label={`Acc Dist % dan perubahan harga harian D${n - 1} sampai D0`}>
          <line x1={padL} x2={W - padR} y1={mid} y2={mid} stroke="currentColor" strokeOpacity={0.25} strokeDasharray="3 3" />
          <text x={padL - 4} y={top + 8} textAnchor="end" fontSize="9" fill={ACC_COLOR}>{fmtMovePct(mA)}</text>
          <text x={padL - 4} y={bottom} textAnchor="end" fontSize="9" fill={ACC_COLOR}>{fmtMovePct(-mA)}</text>
          <text x={W - padR + 4} y={top + 8} fontSize="9" fill={CHG_COLOR}>{fmtMovePct(mC)}</text>
          <text x={W - padR + 4} y={bottom} fontSize="9" fill={CHG_COLOR}>{fmtMovePct(-mC)}</text>
          {segments(dv, yA).map((seg, k) => <polyline key={`a${k}`} points={seg.join(' ')} fill="none" stroke={ACC_COLOR} strokeWidth={1.5} />)}
          {segments(dc, yC).map((seg, k) => <polyline key={`c${k}`} points={seg.join(' ')} fill="none" stroke={CHG_COLOR} strokeWidth={1.5} />)}
          {dates.map((d, i) => (
            <g key={d}>
              {dv[i] != null && <circle cx={x(i)} cy={yA(dv[i]!)} r={i === n - 1 ? 2.8 : 1.6} fill={ACC_COLOR} />}
              {dc[i] != null && <circle cx={x(i)} cy={yC(dc[i]!)} r={i === n - 1 ? 2.8 : 1.6} fill={CHG_COLOR} />}
              <text x={x(i)} y={H - 4} textAnchor="middle" fontSize="9" fill="currentColor" fillOpacity={i === n - 1 ? 0.9 : 0.55}>{dayLabel(d, i)}</text>
              <rect x={x(i) - (W - padL - padR) / (2 * (n - 1))} y={top} width={(W - padL - padR) / (n - 1)} height={bottom - top} fill="transparent">
                <title>{`D${n - 1 - i} · ${dayName(d)} ${fmtYmd(d)}\nAcc Dist: ${fmt(dv[i])}\nChg%: ${fmt(dc[i])}`}</title>
              </rect>
            </g>
          ))}
        </svg>
      </div>
    );
  };

  // The Acc Dist pattern (AI column): its name from the 9 menu columns, and the D10..D0 daily chart (older results: the
  // cumulative line of the 9 columns' Value)
  const shapeBlock = (s: AiStock) => {
    if (!s.tl || s.tv?.length !== 9) return null;
    const daily = dailyChart(s);
    if (daily) {
      return (
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Summary · pola Value W4–D0</div>
          <div className="font-semibold" style={{ color: aiShapeColor(s.tl) }}>{aiShapeName(ai, s)}</div>
          <div className="text-xs text-muted-foreground">{ai?.shapes?.desc[s.tl] ?? ''}</div>
          {daily}
        </div>
      );
    }
    const cols = ai?.shapes?.columns ?? ['W4', 'W3', 'W2', 'W1', 'D4', 'D3', 'D2', 'D1', 'D0'];
    const line = s.tv.reduce<number[]>((acc, v) => [...acc, (acc[acc.length - 1] ?? 0) + v], [0]);
    const lo = Math.min(...line), hi = Math.max(...line), span = hi - lo || 1;
    const W = 300, H = 76, pad = 6, base = H - 14;
    const x = (i: number) => pad + (i * (W - 2 * pad)) / (line.length - 1);
    const y = (v: number) => pad + ((hi - v) / span) * (base - 2 * pad);
    const color = aiShapeColor(s.tl);
    const rp = (jt: number) => (Math.abs(jt) >= 1000 ? `${(jt / 1000).toFixed(1).replace('.', ',')} M` : `${Math.round(jt)} jt`);
    return (
      <div>
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Summary · pola Value W4–D0</div>
        <div className="font-semibold" style={{ color }}>{aiShapeName(ai, s)}</div>
        <div className="text-xs text-muted-foreground">{ai?.shapes?.desc[s.tl] ?? ''}</div>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[76px] mt-1" role="img" aria-label={`Garis akumulasi W4–D0: ${aiShapeName(ai, s)}`}>
          <line x1={pad} x2={W - pad} y1={y(0)} y2={y(0)} stroke="currentColor" strokeOpacity={0.25} strokeDasharray="3 3" />
          <polyline points={line.map((v, i) => `${x(i)},${y(v)}`).join(' ')} fill="none" stroke={color} strokeWidth={1.5} />
          {line.slice(1).map((v, i) => (
            <circle key={i} cx={x(i + 1)} cy={y(v)} r={i === line.length - 2 ? 3 : 1.6} fill={color}>
              <title>{`${colLabel(cols[i] ?? '')}: Value ${rp(s.tv?.[i] ?? 0)}`}</title>
            </circle>
          ))}
          {cols.map((c, i) => (
            <text key={c} x={x(i + 1)} y={H - 2} textAnchor="middle" fontSize="9" fill="currentColor" fillOpacity={0.55}>{colLabel(c)}</text>
          ))}
        </svg>
      </div>
    );
  };

  // Matched historical patterns (naik / waspada) as small cards; one line when none matched
  const patternsBlock = (s: AiStock) => {
    const rows = [...s.pn.map(id => ['naik', id] as const), ...s.pw.map(id => ['waspada', id] as const)].filter(([, id]) => ai?.patterns[id]);
    return (
      <div className="space-y-1">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pola historis yang cocok</div>
        {rows.length === 0 ? <div className="text-xs text-muted-foreground">Tidak ada pola naik atau waspada yang cocok hari ini.</div> : (
          <ul className="space-y-1.5">
            {rows.map(([kind, id]) => {
              const pt = ai!.patterns[id]!;
              return (
                <li key={id} className="rounded-md bg-white/[0.03] px-2 py-1.5">
                  <div className="flex items-start gap-1.5">
                    <span className={`shrink-0 mt-0.5 text-[10px] font-semibold px-1 rounded ${kind === 'naik' ? 'bg-[#00ff00]/15 text-[#00ff00]' : 'bg-[#ffa500]/15 text-[#ffa500]'}`}>{kind}</span>
                    <span className="text-[13px]">{pt.conds.join(' + ')}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                    kena {pct1(pt.hit)} vs saham sejenis {pct1(pt.ctx)} · {pt.n.toLocaleString('id-ID')} kejadian{pt.bandar ? ' · pola bandar' : ''}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const resetSettings = () => {
    setSortConfig(null);
    setStockInput('');
    setSelectedTickers([]);
    setFormSelectedTickers([]);
    setShowStockSuggestions(false);
    setValueFilter('Value');
    setFormValueFilter('Value');
    setPercentDenom('Default');
    setFormPercentDenom('Default');
    setTableFilter('By TLot');
    setFormTableFilter('By TLot');
    setCalcFilter('Daily');
    setFormCalcFilter('Daily');
    setInvFilter('All');
    setFormInvFilter('All');
    setBrokerFilter('All');
    setFormBrokerFilter('All');
    setColumnFilters({});
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1);
  };

  const handleShow = () => {
    setSelectedTickers(formSelectedTickers);
    setValueFilter(formValueFilter);
    setPercentDenom(formPercentDenom);
    setTableFilter(formTableFilter);
    setCalcFilter(formCalcFilter);
    setInvFilter(formInvFilter);
    setBrokerFilter(formBrokerFilter);
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1);
  };

  const handleTickerSelect = (ticker: string) => {
    if (formSelectedTickers.includes(ticker)) {
      setFormSelectedTickers(formSelectedTickers.filter(t => t !== ticker));
    } else {
      setFormSelectedTickers([...formSelectedTickers, ticker]);
    }
  };

  // Presets: the filter row, sort and the table setup as applied by Show (not the form selects before Show), never
  // the dates or the data. Period keys (d0, cw1, …) are already relative to the newest date.
  const presetCurrent = useMemo<FilterSettings>(() => ({
    filters: cleanFilters(columnFilters),
    sort: sortConfig ? { key: sortConfig.key, direction: sortConfig.direction } : null,
    view: {
      tickers: selectedTickers, calc: calcFilter, data: valueFilter, inv: invFilter, broker: brokerFilter,
      ai: aiOn, perPage: itemsPerPage,
    },
  }), [columnFilters, sortConfig, selectedTickers, calcFilter, valueFilter, invFilter, brokerFilter, aiOn, itemsPerPage]);

  // Put a saved setting back: anything missing or invalid in an old preset keeps what is shown now. Selects and the
  // applied view both change (as after Show); only another F/D or broker needs other files, so only then a refetch.
  const applyPreset = (s: FilterSettings) => {
    const { tickers: vTickers, calc: vCalc, data: vData, inv: vInv, broker: vBroker, ai: vAi, perPage: vPerPage } =
      (s?.view && typeof s.view === 'object' ? s.view : {}) as Record<string, unknown>;
    const pick = <T extends string>(x: unknown, allowed: readonly T[], cur: T): T =>
      typeof x === 'string' && (allowed as readonly string[]).includes(x) ? x as T : cur;
    const calc = pick(vCalc, ['Daily', 'Cumulative'] as const, calcFilter);
    const data = pick(vData, ['Value', 'Percent'] as const, valueFilter);
    const inv = pick(vInv, ['All', 'Foreign', 'Domestic'] as const, invFilter);
    const broker = typeof vBroker === 'string'
      && (vBroker === 'All' || brokerList.includes(vBroker) || (!brokerList.length && /^[A-Z0-9]{2,4}$/.test(vBroker)))
      ? vBroker : brokerFilter;
    const tickers = Array.isArray(vTickers)
      ? [...new Set(vTickers.filter((t): t is string => typeof t === 'string' && /^[A-Z0-9]{1,6}$/.test(t)))]
      : selectedTickers;
    const perPage = typeof vPerPage === 'number' && PAGE_SIZES.includes(vPerPage) ? vPerPage : itemsPerPage;
    const sort = s?.sort && typeof s.sort.key === 'string' && s.sort.key && (s.sort.direction === 'asc' || s.sort.direction === 'desc')
      ? { key: s.sort.key, direction: s.sort.direction } : null;

    setColumnFilters(cleanFilters(s?.filters));
    setSelectedTickers(tickers);
    setFormSelectedTickers(tickers);
    setCalcFilter(calc);
    setFormCalcFilter(calc);
    setValueFilter(data);
    setFormValueFilter(data);
    setInvFilter(inv);
    setFormInvFilter(inv);
    setBrokerFilter(broker);
    setFormBrokerFilter(broker);
    setItemsPerPage(perPage);
    // AI on/off through the AI button itself (bootcamp/admin check, loading the results); called before the sort is
    // set so the button's own sort reset never overrides it
    if (typeof vAi === 'boolean' && vAi !== aiOn) void toggleAi();
    const aiAfter = typeof vAi === 'boolean' && canUseAi ? vAi : aiOn;
    setSortConfig(sort && (aiAfter || (sort.key !== 'ai_pola' && sort.key !== 'ai_p')) ? sort : null);
    setCurrentPage(1);
    if (inv !== invFilter || broker !== brokerFilter) setFetchTrigger(prev => prev + 1);
  };



  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="text-center">
          <p className="text-red-500 mb-4 text-sm">Error: {error}</p>
          <Button onClick={() => window.location.reload()} variant="outline" size="sm">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const activeSortedData = tableFilter === 'By FNVol' ? sortedDataForeign : sortedDataTotal;
  const totalPages = Math.ceil(activeSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] gap-2 relative">
      {(isRefetching || loading) && (
        <div className="absolute top-0 left-0 w-full h-1 z-50 overflow-hidden bg-[#3a4252]">
          <div className="w-full h-full bg-blue-500 animate-[loading_1.5s_ease-in-out_infinite]" style={{ transformOrigin: 'left' }}></div>
        </div>
      )}
      <style>{`
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      {/* Top Control Bar */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-1.5 shrink-0 lg:sticky lg:top-0 lg:z-40">
        <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-2 md:gap-x-7 md:gap-y-2">
          
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Ticker:</label>
            <div className="relative flex-1 md:flex-none" ref={dropdownRef}>
              <div className="w-full md:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                  <input
                  type="text"
                  className="w-full bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/50 truncate"
                  placeholder="Select ticker..."
                  value={showStockSuggestions ? stockInput : (formSelectedTickers.length > 0 ? formSelectedTickers.join(', ') : '')}
                  onChange={(e) => {
                    setStockInput(e.target.value);
                    setShowStockSuggestions(true);
                  }}
                  onFocus={() => setShowStockSuggestions(true)}
                />
                <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => setShowStockSuggestions(!showStockSuggestions)} />
              </div>
              {showStockSuggestions && (
                <div id="stock-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-full overflow-hidden flex flex-col">
                  {formSelectedTickers.length > 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
                      <span>Selected ({formSelectedTickers.length})</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormSelectedTickers([]);
                        }}
                        className="text-xs text-destructive hover:text-destructive/80 font-medium"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="max-h-60 overflow-y-auto py-1">
                    {(() => {
                      const filteredList = stockInput === ''
                        ? allTickers.slice(0, 30)
                        : filteredTickers.slice(0, 30);
                      return filteredList.map(stock => {
                        const isSelected = formSelectedTickers.includes(stock);
                        return (
                          <div
                            key={stock}
                            className={`px-3 py-[2.06px] text-sm cursor-pointer hover:bg-accent flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'text-foreground'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTickerSelect(stock);
                            }}
                          >
                            <span>{stock}</span>
                            {isSelected && <span className="text-primary text-xs">✓</span>}
                          </div>
                        );
                      });
                    })()}
                    {stockInput !== '' && filteredTickers.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">No stocks found</div>
                    )}
                    {stockInput === '' && allTickers.length > 30 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t border-[#3a4252]">
                        Type to search {allTickers.length - 30} more stocks
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">View:</label>
            <select
              value={formCalcFilter}
              onChange={(e) => {
                setFormCalcFilter(e.target.value as 'Daily' | 'Cumulative');
              }}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
            >
              <option value="Daily">Daily</option>
              <option value="Cumulative">Cumulative</option>
            </select>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Data:</label>
            <div className="flex items-center gap-1">
              <select
                value={formValueFilter}
                onChange={(e) => {
                  setFormValueFilter(e.target.value as 'Value' | 'Percent');
                }}
                className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
              >
                <option value="Value">Value</option>
                <option value="Percent">%</option>
              </select>
            </div>
          </div>



          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">F/D:</label>
            <select
              value={formInvFilter}
              onChange={(e) => {
                setFormInvFilter(e.target.value as 'All' | 'Foreign' | 'Domestic');
              }}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
            >
              <option value="All">All</option>
              <option value="Foreign">Foreign</option>
              <option value="Domestic">Domestic</option>
            </select>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Broker:</label>
            <div className="relative flex-1 md:flex-none" ref={dropdownBrokerRef}>
              <div
                className="w-full md:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setShowBrokerSuggestions(!showBrokerSuggestions)}
              >
                <span className={`truncate ${formBrokerFilter !== 'All' ? getBrokerColorClass(formBrokerFilter) : ''}`}>{formBrokerFilter === 'All' ? 'ALL (Default)' : formBrokerFilter}</span>
                <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0" />
              </div>
              {showBrokerSuggestions && (
                <div className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-48 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-[#3a4252]">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <input
                        type="text"
                        className="w-full h-8 pl-7 pr-2 bg-[#0a0f20] border border-[#3a4252] rounded text-sm text-white focus:outline-none focus:border-primary/50"
                        placeholder="Search broker..."
                        value={brokerInput}
                        onChange={(e) => setBrokerInput(e.target.value.toUpperCase())}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto py-1">
                    <div
                      className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${formBrokerFilter === 'All' ? 'text-primary font-medium bg-accent/50' : 'text-foreground hover:bg-accent'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormBrokerFilter('All');
                        setBrokerInput('');
                        setShowBrokerSuggestions(false);
                      }}
                    >
                      <span>ALL (Default)</span>
                      {formBrokerFilter === 'All' && <span className="text-primary text-xs">✓</span>}
                    </div>
                    {filteredBrokers.slice(0, 100).map(broker => {
                      const isSelected = formBrokerFilter === broker;
                      return (
                        <div
                          key={broker}
                          className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${isSelected ? 'font-medium bg-accent/50' : 'hover:bg-accent'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormBrokerFilter(broker);
                            setBrokerInput('');
                            setShowBrokerSuggestions(false);
                          }}
                        >
                          <span className={getBrokerColorClass(broker)}>{broker}</span>
                          {isSelected && <span className="text-primary text-xs">✓</span>}
                        </div>
                      );
                    })}
                    {filteredBrokers.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">No brokers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto ml-auto">
            <div className="flex flex-wrap items-center gap-3">
              {/* Sama dengan tombol AI di Broker Summary (Electric Cyan, teks navy); ring = kolom AI sedang tampil */}
              <button
                onClick={toggleAi}
                aria-pressed={aiOn}
                className={`w-8 h-8 rounded-full bg-[#22D3EE] border border-[#67E8F9] flex items-center justify-center hover:shadow-[0_0_10px_rgba(34,211,238,0.55)] transition-all duration-200 shrink-0 ${aiOn ? 'ring-2 ring-[#67E8F9] ring-offset-2 ring-offset-background' : ''}`}
                title={canUseAi ? 'Pola Acc Dist (Summary) dan Peluang per saham' : 'AI — menu khusus bootcamp'}
              >
                <span className="text-[14px] inline-flex items-end leading-none -translate-x-px"><span className="font-extrabold tracking-tight text-[#0A0F20]">Ai</span></span>
              </button>
              <Button onClick={handleShow} className="h-8 text-sm px-4">
                Show
              </Button>
              <Button onClick={resetSettings} variant="outline" size="sm" className="flex items-center gap-2 h-8">
                <Undo2 className="w-4 h-4 rotate-180" />
                Reset
              </Button>
              {/* on phones at the row's right end, so its dropdown (opening leftwards) stays on screen */}
              <div className="ml-auto sm:ml-0">
                <FilterPresets page="accdist" current={presetCurrent} onApply={applyPreset} label={presetLabel} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <Card className="flex flex-col flex-1 min-h-0 p-0 bg-black border-gray-800 overflow-hidden rounded-none w-full animate-pulse">
          <div className="h-[26px] w-full bg-gray-900 border-b border-white/30 shrink-0 flex items-center px-2">
            <div className="h-3 w-16 bg-slate-700/40 rounded"></div>
          </div>
          <div className="flex-1 p-2 space-y-3">
            {/* Header row */}
            <div className="flex gap-2 pb-2 border-b border-white/20">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="h-4 flex-1 bg-slate-700/30 dark:bg-slate-600/50 rounded"></div>
              ))}
            </div>
            {/* Data rows */}
            {[...Array(20)].map((_, i) => (
              <div key={i} className="flex gap-2">
                <div className="h-3.5 w-12 bg-slate-700/20 dark:bg-slate-700/40 rounded"></div>
                {[...Array(11)].map((_, j) => (
                  <div key={j} className="h-3.5 flex-1 bg-slate-700/20 dark:bg-slate-700/40 rounded"></div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      ) : (
      <Card className="flex flex-col flex-1 min-h-0 p-0 bg-black border-gray-800 overflow-hidden rounded-none w-full">
        <div className={`flex flex-col flex-1 min-h-0`}>
          {tableFilter === 'By TLot' && (
            <div className="flex flex-col min-h-0 flex-auto w-full">
               <h3 className="text-[12px] font-bold px-2 py-1 bg-gray-900 text-white border-b border-white/30 shrink-0">By TLot{aiStatus}</h3>
               {renderTable(sortedDataTotal, periodsTotal, false, 0)}
            </div>
          )}

          {tableFilter === 'By FNVol' && (
            <div className="flex flex-col min-h-0 flex-auto w-full">
               <h3 className="text-[12px] font-bold px-2 py-1 bg-gray-900 text-white border-b border-white/30 shrink-0">By FNVol{aiStatus}</h3>
               {renderTable(sortedDataForeign, periodsForeign, true, 1)}
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {(activeSortedData.length > 0) && (
          <div className="border-t border-border bg-muted/30 px-2 py-1 flex flex-col sm:flex-row items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-1 py-0.5 text-[11px] border border-border rounded-md bg-background text-foreground h-6"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>
                Showing {startIndex + 1} - {Math.min(endIndex, activeSortedData.length)} of {activeSortedData.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="First page"
              >
                <ChevronFirst className="w-3 h-3" />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="text-[11px] text-foreground px-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Last page"
              >
                <ChevronLast className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </Card>
      )}

      <MoveTipLayer bind={bindMoveTip} />
      <Dialog open={!!aiDetail} onOpenChange={(open) => { if (!open) setAiDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {aiDetail && ai && !ai.stocks[aiDetail] && ai.skipped?.[aiDetail] && (
            <DialogHeader>
              <DialogTitle>{aiDetail} · AI {fmtYmd(ai.date)}</DialogTitle>
              <DialogDescription>
                Tidak dinilai: {ai.skipped[aiDetail]}. Kondisi saham ini berada di luar data yang dipelajari model,
                jadi probabilitasnya tidak bisa dipercaya dan tidak ditampilkan.
              </DialogDescription>
            </DialogHeader>
          )}
          {aiDetail && ai?.stocks[aiDetail] && (() => {
            const s = ai.stocks[aiDetail]!;
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{aiDetail} · AI {fmtYmd(ai.date)}</DialogTitle>
                  <DialogDescription>{ai.target}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                  {/* "Peluang" = the calibrated Peluang naik, the same number as the column; the AI Prob stays in its detail grid */}
                  <div className="rounded-lg border border-border bg-white/[0.03] p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Peluang</div>
                    <div className="mt-0.5">
                      <span className={`text-3xl font-semibold tabular-nums ${aiProbColor(s.p, ai.base_rate)}`}>{Math.round(s.p * 100)}%</span>
                    </div>
                  </div>
                  {shapeBlock(s)}
                  {ownBlock(s)}
                  {patternsBlock(s)}
                  <div className="text-xs text-muted-foreground border-t border-border pt-2">Alat bantu, bukan rekomendasi beli.</div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
